<script setup lang="ts">
import { Item } from "@models/Item";
import { computed } from "vue";
import Constants from "@/models/Constants";
import AppSwitch from "@/components/AppSwitch.vue";
import AppNumberSelect from "@/components/AppNumberSelect.vue";
import AppInputCurrency from "./AppInputCurrency.vue";

interface Props {
  modelValue: Item[];
  pokerRun: any; // Replace with proper PokerRunEvent type
  includeExtraCard: boolean;
  enableExtraCard: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', items: Item[]): void;
  (e: 'update:includeExtraCard', value: boolean): void;
}>();

const registrationItems = computed(() => {
  return props.modelValue.filter(item => item.itemType === Constants.FEE_TYPE);
});

const customItems = computed(() => {
  return props.modelValue.filter(item => item.itemType === Constants.CUSTOM_TYPE);
});

const extraCardItem = computed(() => {
  return props.pokerRun?.lineItems.find(x => x.itemType === Constants.EXTRA_CARD_TYPE);
});

const donationItems = computed(() => {
  return props.pokerRun?.lineItems.filter(x => x.itemType === Constants.DONATION_TYPE);
});

const updateQuantity = (item: Item, quantity: number) => {
  const updatedItems = props.modelValue.map(i => {
    if (i.itemId === item.itemId) {
      return { ...i, quantity: Math.max(0, quantity) };
    }
    return i;
  });
  emit('update:modelValue', updatedItems);
};

const updateDonation = (item:Item, amount:number) =>
{
    const updatedItems = props.modelValue.map(i => {
    if (i.itemId === item.itemId) {
      if(amount){
        return { ...i, amount: Math.max(0, amount), quantity:1 };
      }
      return { ...i, amount: 0,quantity:0  };
    }
    return i;
  });
  emit('update:modelValue', updatedItems);
}

const updateIncludeExtraCard = (value: boolean) => {
  emit('update:includeExtraCard', value);
};

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '$0.00';
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};
</script>

<template>
  <div class="item-selector">
    <!-- Registration Items Section -->
    <div class="section-header px-2 is-flex is-justify-content-space-between has-background-primary has-text-white">
      <div>Registrations</div>
      <div>QTY</div>
    </div>

    <div v-for="item in registrationItems" :key="item.itemId"
      class="item-row p-2 is-flex is-justify-content-space-between is-align-items-center">
      <div>
        <p class="has-text-weight-bold">{{ item.name }}</p>
        <p class="has-text-grey">{{ formatCurrency(item.amount) }}</p>
      </div>
      <AppNumberSelect :model-value="item.quantity" @update:model-value="(val) => updateQuantity(item, val)" :min="0" />
    </div>

    <!-- Extra Card Option -->
    <div v-if="enableExtraCard && extraCardItem"
      class="item-row p-2 is-flex is-justify-content-space-between is-align-items-center">
      <div>
        <p class="has-text-weight-bold">Extra Card</p>
        <p class="has-text-grey">{{ formatCurrency(extraCardItem.amount) }}</p>
        <p class="help">Add an extra card for each registration</p>
      </div>
      <div>
        <AppSwitch toggleId="includeExtraCard" :modelValue="includeExtraCard"
          @update:modelValue="updateIncludeExtraCard" />
      </div>
    </div>

    <!-- Custom Items Section -->
    <div v-if="(customItems && customItems.length > 0) || (donationItems && donationItems.length > 0)">
      <div
        class="section-header px-2 mt-4 is-flex is-justify-content-space-between has-background-primary has-text-white">
        <div>Additional Items</div>
        <div>QTY</div>
      </div>

      <div v-for="item in customItems" :key="item.itemId"
        class="item-row p-2 is-flex is-justify-content-space-between is-align-items-center">
        <div>
          <p class="has-text-weight-bold">{{ item.name }}</p>
          <p class="has-text-grey">{{ formatCurrency(item.amount) }}</p>
        </div>
        <AppNumberSelect :model-value="item.quantity" @update:model-value="(val) => updateQuantity(item, val)"
          :min="0" />
      </div>

      <div v-for="item in donationItems" :key="`donation-${item.itemId}`"
        class="item-row p-2 is-flex is-justify-content-space-between is-align-items-center">
        <div>
          <p class="has-text-weight-bold">{{ item.name }}</p>
          <p class="has-text-grey">Other</p>
        </div>
        <AppInputCurrency class="has-text-right" style="width:100px" currency="usd" :model-value="item.amount"
          @update:model-value="(val) => updateDonation(item, val)" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.item-selector {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.section-header {
  padding: 0.5rem 1rem;
  font-weight: bold;
}

.item-row {
  border-bottom: 1px solid #eee;
}

.item-row:last-child {
  border-bottom: none;
}

.help {
  font-size: 0.875rem;
  color: #666;
}
</style>
